import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Send, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { setError("Please fill in all required fields."); return; }
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/api/contact`, form);
      setSent(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to send message.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="text-center max-w-md px-4">
          <CheckCircle className="h-12 w-12 text-[#39FF14] mx-auto mb-6" />
          <h1 className="text-2xl font-bold uppercase text-[#F2F2F2] mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Message Sent</h1>
          <p className="text-[#A1A1A1] text-sm">Thank you for reaching out. We'll get back to you as soon as possible.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8">
          <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Support</p>
          <h1 data-testid="contact-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>Contact Us</h1>
          <p className="text-[#A1A1A1] text-sm mt-3">Have a question about an order, preorder, or product? Send us a message and we'll get back to you.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14]" data-testid="contact-name" />
            </div>
            <div className="space-y-1">
              <Label className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14]" data-testid="contact-email" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">Subject</Label>
            <Input value={form.subject} onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="General Inquiry" className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14]" data-testid="contact-subject" />
          </div>
          <div className="space-y-1">
            <Label className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">Message *</Label>
            <textarea value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} rows={5} className="w-full bg-[#0D0D0D] border border-[#1A1A1A] text-[#F2F2F2] px-3 py-2 text-sm focus:border-[#39FF14] focus:outline-none focus:ring-1 focus:ring-[#39FF14] resize-none" data-testid="contact-message" />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-[0.15em] text-xs font-bold py-6">
            <Send className="h-4 w-4 mr-2" />{sending ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </div>
    </div>
  );
}
